import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { SDK_INFO, type SdkKey } from '@gonfalon/sdk-info/sdkInfo';
import { Alert, Heading, Link, Text } from '@launchpad-ui/components';

import { trackSdkSetupSnippetClicked } from '../../../analytics';
import { SdkSetupSnippet } from '../../SdkSetupSnippet/SdkSetupSnippet';

import { experimentationSnippetConfig } from './config';
import type { SdkWithExperimentSnippets } from './types';

import baseStyles from '../SdkSetupSnippets.module.css';
import styles from './Experimentation.module.css';

interface ExperimentationProps {
  sdk: SdkWithExperimentSnippets;
}

export const isExperimentationSupported = (sdk: SdkKey): sdk is SdkWithExperimentSnippets => {
  return sdk in experimentationSnippetConfig;
};

export function Experimentation({ sdk }: ExperimentationProps) {
  const snippet = experimentationSnippetConfig[sdk];
  return (
    <div className={styles.experimentation}>
      <div className={baseStyles.labeledSection}>
        <Heading level={3}>Send metric events</Heading>
        <span className={styles.description}>
          LaunchDarkly attributes each event to the flag variations the context saw, which is how experiments and
          guarded releases are measured.{' '}
          <Link href={getDocumentationUrl('/home/metrics')} target="_blank">
            Read about metrics
          </Link>
        </span>
      </div>
      <div className={baseStyles.snippetSection}>
        <SdkSetupSnippet
          withCopyButton
          trackAnalyticsOnClick={() => trackSdkSetupSnippetClicked(snippet.event)}
          snippets={[
            {
              title: snippet.title,
              snippet: SDK_INFO[sdk].experimentationSnippet.trackOnly,
              language: SDK_INFO[sdk].prismLang,
            },
          ]}
          withLineNumbers
        />

        <Alert className={styles.alert} status="info">
          <Text>
            <ul>
              <li>
                <span>
                  Some architectures need additional setup to track experiments accurately.{' '}
                  <Link href={getDocumentationUrl('/home/metrics')} target="_blank">
                    Read about common cases
                  </Link>
                </span>
              </li>
              <li>
                <span>
                  Warehouse native experiments do not require SDKs.{' '}
                  <Link href={getDocumentationUrl('/home/warehouse-native/metrics')} target="_blank">
                    Read about warehouse native experimentation
                  </Link>
                </span>
              </li>
            </ul>
          </Text>
        </Alert>
      </div>
    </div>
  );
}
